import React from 'react';
import moment from 'moment';
import { Bar } from 'react-chartjs-2';
import { Table } from 'reactstrap';

import { formatNumber, formatMs } from '../helpers/format';
import { analyzeReplicas, replicaLabel, isKnownReplica } from '../helpers/externalApiReplicas';
import { CLIENT_COLORS, OTHER_COLOR } from '../helpers/externalApiOverview';
import { DistributionChart } from './externalApiReplicas';

/**
* "Replicas" tab of the "API analytics (all requests)" page: how the swarm
* splits all backend requests, one row per replica (swarm slot), from
* `overview.byReplica` and `timeseries[].replicas`.
*
* Color: a replica is an identity, so each slot keeps its categorical color
* by slot number (slot 1 = first color, ...) - the same replica is the same
* color whatever else is on the chart; old logs without a slot are neutral
* gray. Flags use the reserved status colors with an icon and a reason.
*/

const palette = {
    surface: '#FFFFFF',
    border: '#E6EDF5',
    textPrimary: '#3A3F56',
    textSecondary: '#52514E',
    textMuted: '#818FA5',
    good: '#0ca30c',
    critical: '#d03b3b',
};

const SEVERITY = {
    warning: { icon: '▲', color: '#a86f00', rowBg: '#FFF8E6' },
    critical: { icon: '●', color: palette.critical, rowBg: '#FDEEEE' },
};

const styles = {
    kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 20 },
    tile: { backgroundColor: palette.surface, borderRadius: 6, padding: '14px 16px', boxShadow: '0 10px 20px rgba(0,0,0,0.06)' },
    tileLabel: { fontSize: 11, fontWeight: 600, color: palette.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 },
    tileValue: { fontSize: 22, fontWeight: 600, color: palette.textPrimary, lineHeight: 1.1 },
    tileSub: { fontSize: 12, color: palette.textSecondary, marginTop: 4 },
    card: { backgroundColor: palette.surface, borderRadius: 6, padding: '16px 18px', boxShadow: '0 10px 20px rgba(0,0,0,0.06)', marginBottom: 20 },
    sectionTitle: { fontSize: 13, fontWeight: 700, color: palette.textPrimary, marginBottom: 10 },
    note: { fontSize: 12, color: palette.textMuted, marginTop: 6 },
    legendRow: { display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 10 },
    legendItem: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: palette.textSecondary },
    swatch: { width: 10, height: 10, borderRadius: 2, flexShrink: 0 },
    issue: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, marginRight: 10, whiteSpace: 'nowrap' },
    emptyState: { textAlign: 'center', padding: '30px 20px', color: palette.textMuted, fontSize: 13 },
};

const isNum = (v) => v !== null && v !== undefined && !isNaN(Number(v));
const dash = (v, fmt) => (isNum(v) ? fmt(v) : '—');
const pct = (v) => dash(v, (x) => `${Number(x).toFixed(1)}%`);
const ms = (v) => dash(v, formatMs);
const fixed = (digits) => (v) => dash(v, (x) => Number(x).toFixed(digits));

/** slot 1 → first categorical color, ...; old logs (no slot) → gray */
export const replicaColor = (replica) => (
    isKnownReplica(replica) && Number(replica) > 0 ? CLIENT_COLORS[(Number(replica) - 1) % CLIENT_COLORS.length] : OTHER_COLOR
);

/** spread between the most and the least loaded replica, in percentage points */
const UNEVEN = [
    { max: 10, color: palette.good, icon: '✓', label: 'Even' },
    { max: 25, color: '#a86f00', icon: '▲', label: 'Somewhat uneven' },
    { max: Infinity, color: palette.critical, icon: '●', label: 'Uneven' },
];

function Tile({ label, value, sub, valueStyle }) {
    return (
        <div style={styles.tile}>
            <div style={styles.tileLabel}>{label}</div>
            <div style={{ ...styles.tileValue, ...valueStyle }}>{value}</div>
            {sub ? <div style={styles.tileSub}>{sub}</div> : null}
        </div>
    );
}

function Issues({ issues, lang }) {
    if (!issues.length) return <span style={{ color: palette.textMuted }}>{'OK'.translate(lang)}</span>;
    let seen = {};
    return issues.filter((i) => (seen[i.reason] ? false : (seen[i.reason] = true))).map((i) => (
        <span key={i.reason} style={{ ...styles.issue, color: SEVERITY[i.severity].color }}>
            <span aria-hidden="true">{SEVERITY[i.severity].icon}</span>
            {i.reason.translate(lang)}
        </span>
    ));
}

const timeLabel = (at, interval, count) => (
    interval === 'hour' ? moment(at).format('MM-DD HH:mm') : moment.utc(at).format(count > 120 ? 'YYYY-MM-DD' : 'MM-DD')
);

/** requests per replica per interval, stacked */
function ReplicaSeriesChart({ timeseries, interval, lang }) {
    let rows = timeseries || [];
    let keys = {};
    rows.forEach((p) => Object.keys(p.replicas || {}).forEach((k) => { if ((p.replicas[k] || 0) > 0) keys[k] = true; }));
    let slots = Object.keys(keys).filter((k) => k !== 'unknown').sort((a, b) => Number(a) - Number(b));
    if (keys.unknown) slots.push('unknown');

    if (!slots.length) {
        return <div style={styles.emptyState}>{'No per-replica data in this period yet.'.translate(lang)}</div>;
    }

    let label = (k) => (k === 'unknown' ? 'Unknown (old logs)'.translate(lang) : `R${k}`);
    let datasets = slots.map((k) => ({
        label: label(k),
        data: rows.map((p) => (p.replicas && p.replicas[k]) || 0),
        backgroundColor: replicaColor(k === 'unknown' ? null : k),
        borderColor: palette.surface,
        borderWidth: { top: 1 },
        borderSkipped: false,
        stack: 'replicas',
    }));

    return (
        <>
            <div style={{ height: 280 }}>
                <Bar
                    data={{ labels: rows.map((p) => timeLabel(p.at, interval, rows.length)), datasets }}
                    options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { mode: 'index', intersect: false },
                        plugins: {
                            legend: { display: false },
                            tooltip: { filter: (item) => item.parsed.y > 0, callbacks: { label: (ctx) => `${ctx.dataset.label}: ${formatNumber(ctx.parsed.y)}` } },
                        },
                        scales: {
                            x: { stacked: true, grid: { display: false }, ticks: { color: palette.textMuted, font: { size: 11 }, maxRotation: 0, autoSkip: true } },
                            y: { stacked: true, beginAtZero: true, grid: { color: palette.border }, ticks: { color: palette.textMuted, font: { size: 11 }, precision: 0 } },
                        },
                    }}
                />
            </div>
            <div style={styles.legendRow}>
                {slots.map((k) => (
                    <span key={k} style={styles.legendItem}>
                        <span style={{ ...styles.swatch, backgroundColor: replicaColor(k === 'unknown' ? null : k) }} />
                        {label(k)}
                    </span>
                ))}
            </div>
        </>
    );
}

/**
* @param {object} data - the `/overview` response
* @param {function} [onOpenReplica] - called with the slot number; the page opens the Logs tab for it
*/
function ApiLogsReplicasTab({ data, onOpenReplica, lang }) {
    let byReplica = data.byReplica || [];
    let { rows, replicaCount, idealPercent } = analyzeReplicas(byReplica, { durationKey: 'p95Ms' });
    let known = rows.filter((r) => isKnownReplica(r.replica));

    if (!byReplica.length) {
        return <div style={styles.emptyState}>{'No replica data in this period.'.translate(lang)}</div>;
    }

    let maxBy = (key) => known.reduce((best, r) => (isNum(r[key]) && (!best || Number(r[key]) > Number(best[key])) ? r : best), null);
    let busiest = maxBy('requestSharePercent');
    let slowest = maxBy('p95Ms');
    let shares = known.map((r) => Number(r.requestSharePercent)).filter((v) => !isNaN(v));
    let spread = shares.length >= 2 ? Math.max(...shares) - Math.min(...shares) : null;
    let uneven = spread === null ? null : UNEVEN.find((u) => spread <= u.max);
    let isMinute = data.interval === 'minute';

    return (
        <div>
            <div style={styles.kpiRow}>
                <Tile label={'Replicas'.translate(lang)} value={formatNumber(replicaCount)} sub={rows.some((r) => !isKnownReplica(r.replica)) ? 'plus old logs without a slot'.translate(lang) : null} />
                <Tile label={'Busiest'.translate(lang)} value={busiest ? replicaLabel(busiest.replica, lang) : '—'} sub={busiest ? `${pct(busiest.requestSharePercent)} ${'of requests'.translate(lang)}` : null} />
                <Tile label={'Slowest'.translate(lang)} value={slowest ? replicaLabel(slowest.replica, lang) : '—'} sub={slowest ? `p95 ${ms(slowest.p95Ms)}` : null} />
                <Tile
                    label={'Unevenness'.translate(lang)}
                    value={spread === null ? '—' : `${spread.toFixed(1)} pp`}
                    valueStyle={uneven ? { color: uneven.color } : undefined}
                    sub={uneven ? <span style={{ color: uneven.color, fontWeight: 600 }}>{uneven.icon} {uneven.label.translate(lang)} <span style={{ color: palette.textMuted, fontWeight: 400 }}>{'(max − min share)'.translate(lang)}</span></span> : null}
                />
            </div>

            <div style={styles.card}>
                <div style={styles.sectionTitle}>{'Requests per replica'.translate(lang)}</div>
                {isMinute
                    ? <div style={styles.emptyState}>{'Per replica is available per hour / day - switch the chart on the Overview tab.'.translate(lang)}</div>
                    : <ReplicaSeriesChart timeseries={data.timeseries} interval={data.interval} lang={lang} />}
            </div>

            {replicaCount >= 2 ? (
                <div style={styles.card}>
                    <div style={styles.sectionTitle}>{'Distribution'.translate(lang)}</div>
                    <DistributionChart rows={rows} idealPercent={idealPercent} lang={lang} />
                    <div style={styles.note}>{'Share of requests among replicas with a slot; the dashed line is the ideal (100 / number of replicas).'.translate(lang)}</div>
                </div>
            ) : null}

            <div style={styles.card}>
                <div style={styles.sectionTitle}>{'Replicas'.translate(lang)}</div>
                <div style={{ ...styles.note, marginTop: 0, marginBottom: 10 }}>{'Click a replica to see its logs from the last 24 h.'.translate(lang)}</div>
                <Table responsive hover size="sm" style={{ fontSize: 13 }}>
                    <thead>
                        <tr>
                            <th>{'Replica'.translate(lang)}</th>
                            <th>{'Requests'.translate(lang)}</th>
                            <th>{'Req/s'.translate(lang)}</th>
                            <th title={'Share of the total processing time'.translate(lang)}>{'Time share'.translate(lang)}</th>
                            <th>{'Avg'.translate(lang)}</th>
                            <th>p95</th>
                            <th>{'Slow %'.translate(lang)}</th>
                            <th>{'Errors %'.translate(lang)}</th>
                            <th>5xx</th>
                            <th title={'How busy this replica was with requests (% of time; can go over 100% since requests overlap)'.translate(lang)}>{'Busy %'.translate(lang)}</th>
                            <th title={'Other requests being processed when a request arrived'.translate(lang)}>{'In flight avg / max'.translate(lang)}</th>
                            <th title={'Event loop lag (process congestion)'.translate(lang)}>{'Lag avg / max'.translate(lang)}</th>
                            <th>{'Avg queries'.translate(lang)}</th>
                            <th>{'Status'.translate(lang)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => {
                            let isKnown = isKnownReplica(row.replica);
                            let clickable = isKnown && !!onOpenReplica;
                            let sev = row.severity ? SEVERITY[row.severity] : null;
                            return (
                                <tr
                                    key={isKnown ? row.replica : 'unknown'}
                                    style={{ cursor: clickable ? 'pointer' : 'default', color: isKnown ? undefined : palette.textMuted, backgroundColor: sev ? sev.rowBg : undefined }}
                                    title={clickable ? 'Open the logs for this replica'.translate(lang) : undefined}
                                    onClick={() => clickable && onOpenReplica(row.replica)}
                                >
                                    <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                        <span style={{ ...styles.swatch, display: 'inline-block', marginRight: 6, backgroundColor: replicaColor(row.replica) }} />
                                        {replicaLabel(row.replica, lang)}
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        {formatNumber(row.requests)}
                                        <span style={{ color: palette.textMuted, fontSize: 11 }}> ({pct(row.requestSharePercent)})</span>
                                    </td>
                                    <td>{fixed(3)(row.avgPerSecond)}</td>
                                    <td>{pct(row.timeSharePercent)}</td>
                                    <td>{ms(row.avgMs)}</td>
                                    <td>{ms(row.p95Ms)}</td>
                                    <td>{pct(row.slowPercent)}</td>
                                    <td>{pct(row.errorPercent)}</td>
                                    <td>{dash(row.errors5xx, formatNumber)}</td>
                                    <td>{pct(row.busyPercent)}</td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{fixed(1)(row.avgInFlight)} / {dash(row.maxInFlight, formatNumber)}</td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{ms(row.avgEventLoopLagMs)} / {ms(row.maxEventLoopLagMs)}</td>
                                    <td>{fixed(1)(row.avgDbQueries)}</td>
                                    <td>{isKnown ? <Issues issues={row.issues} lang={lang} /> : '—'}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>
        </div>
    );
}

export default ApiLogsReplicasTab;
